import { portalApi } from '../../../api';
import { Statuses } from '../../../constants/statuses';
import { sanitizeBlacklist } from '../../../utils/sanitizers';

/* ACTIONS
========================================================= */
const DOCS_CLEAR_DOCUMENTS = 'DOCS_CLEAR_DOCUMENTS';

const DOCS_RESET_ASSOCIATED_POLICIES = 'DOCS_RESET_ASSOCIATED_POLICIES';

const DOCS_UPDATE_DOCUMENT_POLICIES = 'DOCS_UPDATE_DOCUMENT_POLICIES';
const DOCS_UPDATE_DOCUMENT_POLICIES_SUCCESS = 'DOCS_UPDATE_DOCUMENT_POLICIES_SUCCESS';
const DOCS_UPDATE_DOCUMENT_POLICIES_FAILURE = 'DOCS_UPDATE_DOCUMENT_POLICIES_FAILURE';

const DOCUMENTS_SET_CUSTOM_EXPIRATION_DATE = 'DOCUMENTS_SET_CUSTOM_EXPIRATION_DATE';
const DOCUMENTS_SET_CUSTOM_EXPIRATION_DATE_SUCCESS = 'DOCUMENTS_SET_CUSTOM_EXPIRATION_DATE_SUCCESS';
const DOCUMENTS_SET_CUSTOM_EXPIRATION_DATE_FAILURE = 'DOCUMENTS_SET_CUSTOM_EXPIRATION_DATE_FAILURE';

const DOCS_CREATE_DOC = 'DOCS_CREATE_DOC';
const DOCS_CREATE_DOC_SUCCESS = 'DOCS_CREATE_DOC_SUCCESS';
const DOCS_CREATE_DOC_FAILURE = 'DOCS_CREATE_DOC_FAILURE';

const DOCS_GENERATE_S3_PUT_URL = 'DOCS_GENERATE_S3_PUT_URL';
const DOCS_GENERATE_S3_PUT_URL_SUCCESS = 'DOCS_GENERATE_S3_PUT_URL_SUCCESS';
const DOCS_GENERATE_S3_PUT_URL_FAILURE = 'DOCS_GENERATE_S3_PUT_URL_FAILURE';

const DOCS_UPDATE_DOC = 'DOCS_UPDATE_DOC';
const DOCS_UPDATE_DOC_SUCCESS = 'DOCS_UPDATE_DOC_SUCCESS';
const DOCS_UPDATE_DOC_FAILURE = 'DOCS_UPDATE_DOC_FAILURE';

const DOCS_PREPARE_DOC = 'DOCS_PREPARE_DOC';
const DOCS_PREPARE_DOC_SUCCESS = 'DOCS_PREPARE_DOC_SUCCESS';
const DOCS_PREPARE_DOC_FAILURE = 'DOCS_PREPARE_DOC_FAILURE';

const DOCS_GENERATE_S3_GET_URL = 'DOCS_GENERATE_S3_GET_URL';
const DOCS_GENERATE_S3_GET_URL_SUCCESS = 'DOCS_GENERATE_S3_GET_URL_SUCCESS';
const DOCS_GENERATE_S3_GET_URL_FAILURE = 'DOCS_GENERATE_S3_GET_URL_FAILURE';

export const clearDocuments = () => dispatch => {
  dispatch({ type: DOCS_CLEAR_DOCUMENTS });
};

export const createDocumentForRecord = (recordId, data) => async dispatch => {
  dispatch({ type: DOCS_CREATE_DOC, recordId, data });

  try {
    const response = await portalApi.createRecordDocument(recordId, data);
    dispatch({ type: DOCS_CREATE_DOC_SUCCESS, response });
    return response;
  } catch (error) {
    dispatch({ type: DOCS_CREATE_DOC_FAILURE, error });
    throw error;
  }
};

/**
 * updateDocument
 *
 * @param docId
 * @param data
 * @param {function(*)} onSuccess
 * @param {function(*)} onFail
 * @returns {function(*): Promise<Response>}
 * @throws Error
 */
export const updateDocument = (docId, data, onSuccess = () => {}, onFail = () => {}) => async dispatch => {
  dispatch({ type: DOCS_UPDATE_DOC, docId, data });
  let document = null;

  try {
    document = await portalApi.updateDocument(docId, data);

    dispatch({ type: DOCS_UPDATE_DOC_SUCCESS, document });
    onSuccess();
  } catch (error) {
    dispatch({ type: DOCS_UPDATE_DOC_FAILURE, error });
    onFail();
  }

  return document;
};

/**
 * We use prepareDocument during the certificate flow to create the document ahead of submission with a CREATED status.
 * On certificate submission, we will update the status to ACTIVE and link to any policies indicated
 */
export const prepareDocument = (docId, data, onSuccess = () => {}, onFail = () => {}) => async dispatch => {
  dispatch({ type: DOCS_PREPARE_DOC, docId, data });
  let document = null;

  try {
    document = await portalApi.prepareDocument(docId, data);

    dispatch({ type: DOCS_PREPARE_DOC_SUCCESS, document });
    onSuccess();
  } catch (error) {
    dispatch({ type: DOCS_PREPARE_DOC_FAILURE, error });
    onFail();
  }

  return document;
};

export const generateS3GetUrl = docId => async dispatch => {
  dispatch({ type: DOCS_GENERATE_S3_GET_URL, docId });

  try {
    const response = await portalApi.generateS3GetUrlForDocument(docId);
    dispatch({ type: DOCS_GENERATE_S3_GET_URL_SUCCESS, response });
    return response[0];
  } catch (error) {
    dispatch({ type: DOCS_GENERATE_S3_GET_URL_FAILURE, error });
  }
};

const DOCS_TOGGLE_DOC_TYPE_EXPANDED = 'DOCS_TOGGLE_DOC_TYPE_EXPANDED';
export const toggleDocTypeExpanded = docTypeId => dispatch => {
  dispatch({ type: DOCS_TOGGLE_DOC_TYPE_EXPANDED, docTypeId });
};

/* STATE
========================================================= */
const DEFAULT_STATE = {
  documentRequirements: [],
  documents: [],
  expandedDocTypes: {},
  associatedPolicies: [],
  associatedPoliciesChecked: [],
  associatedPoliciesCheckAllChecked: false,
  currentDocument: '',
  loading: false,
  docPolicyLinkIsProcessing: false
};
/* REDUCER
========================================================= */
const documentReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case DOCS_TOGGLE_DOC_TYPE_EXPANDED: {
      if (state.expandedDocTypes[action.docTypeId]) {
        return {
          ...state,
          expandedDocTypes: sanitizeBlacklist(state.expandedDocTypes, [action.docTypeId.toString()])
        };
      }

      return {
        ...state,
        expandedDocTypes: {
          ...state.expandedDocTypes,
          [action.docTypeId]: true
        }
      };
    }

    case DOCS_RESET_ASSOCIATED_POLICIES:
      return {
        ...state,
        associatedPolicies: [],
        associatedPoliciesChecked: [],
        associatedPoliciesCheckAllChecked: false
      };

    case DOCS_CREATE_DOC:
      return state;

    case DOCS_CREATE_DOC_SUCCESS:
      return state;

    case DOCS_CREATE_DOC_FAILURE:
      return { ...state, error: action.error };

    case DOCS_GENERATE_S3_PUT_URL:
      return state;

    case DOCS_GENERATE_S3_PUT_URL_SUCCESS:
      return state;

    case DOCS_GENERATE_S3_PUT_URL_FAILURE:
      return { ...state, error: action.error };

    case DOCS_UPDATE_DOC:
      return state;

    case DOCS_UPDATE_DOCUMENT_POLICIES: {
      return {
        ...state,
        docPolicyLinkIsProcessing: true
      };
    }

    case DOCS_UPDATE_DOCUMENT_POLICIES_SUCCESS: {
      return {
        ...state,
        docPolicyLinkIsProcessing: false
      };
    }

    case DOCS_UPDATE_DOCUMENT_POLICIES_FAILURE: {
      return {
        ...state,
        docPolicyLinkIsProcessing: false
      };
    }

    case DOCUMENTS_SET_CUSTOM_EXPIRATION_DATE: {
      return {
        ...state,
        docPolicyLinkIsProcessing: true
      };
    }

    case DOCUMENTS_SET_CUSTOM_EXPIRATION_DATE_SUCCESS: {
      return {
        ...state,
        docPolicyLinkIsProcessing: false
      };
    }

    case DOCUMENTS_SET_CUSTOM_EXPIRATION_DATE_FAILURE: {
      return {
        ...state,
        docPolicyLinkIsProcessing: false
      };
    }

    case DOCS_UPDATE_DOC_SUCCESS: {
      const updatedDocument = action.document;
      const docTypeId = updatedDocument.insuranceDocumentTypeId;
      const docId = updatedDocument.id;

      const updatedDocuments = state.documents.map(doc => {
        if (doc.id === parseInt(docId, 10)) {
          return updatedDocument;
        }

        return doc;
      });

      const updatedDocumentRequirements = state.documentRequirements.map(docReq => {
        if (docReq.insuranceDocumentTypeId === parseInt(docTypeId, 10)) {
          const updatedDocs = docReq.documents.map(doc => {
            if (doc.id === parseInt(docId, 10)) {
              return updatedDocument;
            }

            return doc;
          });
          return { ...docReq, documents: updatedDocs };
        }

        return docReq;
      });

      return {
        ...state,
        documents: updatedDocuments,
        documentRequirements: updatedDocumentRequirements
      };
    }

    case DOCS_UPDATE_DOC_FAILURE:
      return { ...state, error: action.error };

    case DOCS_CLEAR_DOCUMENTS:
      return { ...DEFAULT_STATE };

    default:
      return state;
  }
};

// Selectors
export const selectDocumentRequirements = state => state.documents.documentRequirements;
export const selectDocumentRequirementExpanded = state => state.documents.expandedDocTypes;
export const selectDocuments = state => state.documents.documents;
export const selectAllActiveDocs = state => state.documents.documents
  .filter(doc => ![Statuses.Deleted, Statuses.Archived].includes(doc.status));
export const selectDocument = docId => state => state.documents.documents.find(doc => doc.id === parseInt(docId, 10));
export const selectAreDocumentsLoading = state => state.documents.loading;
export const selectCurrentDocument = state => state.documents.currentDocument;
export const selectCustomExpirationDateChecked = state => !!state.currentDocument.expiresAt;
export const selectDocPolicyLinkIsProcessing = state => state.documents.docPolicyLinkIsProcessing;
export const selectNonRequiredDocumentTypesWithDocuments = state => state.documents.nonRequiredDocumentTypesWithDocuments;

export default documentReducer;
